import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  THALAMOS_ADMIN_ROLE,
  type ActivityEntry,
} from "@thalamos/haven-common";

/**
 * A date in words, and a time to the minute.
 *
 * The day is a heading over the entries that fall in it, so a row itself only
 * has to say where in the day it sits — which is also what stops a burst of
 * changes made in one sitting repeating the same date down the page.
 */
const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });
const TIME_OF_DAY = new Intl.DateTimeFormat("en-GB", { timeStyle: "short" });

export type Translate = (
  key: string,
  options?: Record<string, unknown>,
) => string;

/**
 * What one entry says about what was acted on, as the log it appears in reads
 * it. Who acted, as what and when is said the same way in every log, so it is
 * not part of this.
 */
export type DescribedEntry = {
  subject: string;
  /** A mark beside the subject for the act itself, or null where the subject already says it. */
  change: { label: string; description: string; ended: boolean } | null;
  /**
   * How the words held beside the entry are introduced: as the reason for the
   * act, or, where a cause from a closed set already says why, as additional
   * information. The reason where left out.
   */
  reasonAs?: "reason" | "additional-information";
};

/** The session context somebody acted in: an organisation, and the roles they held at it. */
const sessionContextOf = (entry: ActivityEntry, t: Translate): string => {
  if (entry.organisationName === null || entry.organisationName === undefined) {
    return t("activity.noSessionContext");
  }

  // A Thalamos admin acts for no customer, so is named by the role alone.
  if (entry.roles?.includes(THALAMOS_ADMIN_ROLE) === true) {
    return t(`roles.${THALAMOS_ADMIN_ROLE}`);
  }

  return entry.roles === null ||
    entry.roles === undefined ||
    entry.roles.length === 0
    ? entry.organisationName
    : t("activity.sessionContextLabel", {
        organisation: entry.organisationName,
        // Through the same names the rest of the product calls roles by. A role
        // retired from the product still happened, so an unknown one reads back
        // as whatever was stored rather than as a missing key.
        role: entry.roles
          .map((role) => t(`roles.${role}`, { defaultValue: role }))
          .join(", "),
      });
};

/** The entries of one day, in the order they were given. */
type Day = { day: string; entries: ActivityEntry[] };

const byDay = (entries: readonly ActivityEntry[]): Day[] =>
  entries.reduce<Day[]>((days, entry) => {
    const day = CALENDAR_DATE.format(entry.timestamp);
    const current = days.at(-1);

    if (current !== undefined && current.day === day) {
      current.entries.push(entry);
      return days;
    }

    return [...days, { day, entries: [entry] }];
  }, []);

const Entry = ({
  entry,
  described,
}: {
  entry: ActivityEntry;
  described: DescribedEntry;
}) => {
  const { t } = useTranslation();

  const said = {
    who: entry.userName ?? t("activity.system"),
    sessionContext: sessionContextOf(entry, t),
    time: TIME_OF_DAY.format(entry.timestamp),
  };

  return (
    <Stack
      component="li"
      direction="row"
      spacing={2}
      useFlexGap
      flexWrap="wrap"
      justifyContent="space-between"
      alignItems="baseline"
      sx={{ py: 1 }}
    >
      {/* `minWidth: 0` lets a name with nowhere to break, such as an email
          address, shrink instead of pushing the row wider than its container. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ overflowWrap: "anywhere" }}
        >
          {described.subject}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          aria-label={t("activity.attributionLabel", said)}
          sx={{ overflowWrap: "anywhere" }}
        >
          {t("activity.attribution", said)}
        </Typography>
        {entry.reason === null || entry.reason === undefined ? null : (
          <Typography
            variant="body2"
            sx={{ mt: 0.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {t(
              match(described.reasonAs ?? "reason")
                .with("reason", () => "activity.reason")
                .with(
                  "additional-information",
                  () => "activity.additionalInformation",
                )
                .exhaustive(),
              { reason: entry.reason },
            )}
          </Typography>
        )}
      </Box>

      {described.change === null ? null : (
        <Typography
          variant="caption"
          color={described.change.ended ? "warning.main" : "text.secondary"}
          aria-label={described.change.description}
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
          {described.change.label}
        </Typography>
      )}
    </Stack>
  );
};

export type ActivityLogProps = {
  entries: readonly ActivityEntry[];
  count: number;
  loading: boolean;
  errored: boolean;
  /** Said in place of the entries when there are none. */
  empty: string;
  describe: (entry: ActivityEntry, t: Translate) => DescribedEntry;
  /**
   * Asks for more of the log. Absent where no further page can be asked for,
   * which is also what makes the count line say which part of the log is held.
   */
  onShowMore?: () => Promisable<void>;
};

/**
 * A log of what was done to something, in the order it was given, grouped by day.
 *
 * What an entry is about differs by log and is the caller's `describe`; who
 * acted, in what session context and when is said the same way in all of them.
 */
export const ActivityLog = ({
  entries,
  count,
  loading,
  errored,
  empty,
  describe,
  onShowMore,
}: ActivityLogProps) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={2}>
      {errored ? (
        <Alert severity="error">{t("activity.loadFailed")}</Alert>
      ) : null}

      {loading ? <CircularProgress aria-label={t("common.loading")} /> : null}

      {!loading && !errored && entries.length === 0 ? (
        // Said outright rather than left blank: an empty log reads as broken,
        // and "nothing happened" is a real and useful answer.
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      ) : null}

      {/* One layout at every width: a log is a sequence rather than a
          comparison, so there is nothing for columns to line up. */}
      {byDay(entries).map((day) => (
        <Box key={day.day}>
          <Typography variant="overline" color="text.secondary" display="block">
            {day.day}
          </Typography>

          <Stack
            component="ul"
            divider={<Divider component="li" />}
            sx={{ listStyle: "none", m: 0, p: 0 }}
          >
            {day.entries.map((entry) => (
              <Entry
                key={entry.id}
                entry={entry}
                described={describe(entry, t)}
              />
            ))}
          </Stack>
        </Box>
      ))}

      {/* An account of what was done is not an answer if it stops at the most
          recent page, so how much of it is held is always said, and the rest
          asked for while there is more to ask for. */}
      {count > entries.length ? (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Typography variant="caption" color="text.secondary">
            {t(
              onShowMore === undefined
                ? "activity.showingMostRecent"
                : "activity.showing",
              { shown: entries.length, total: count },
            )}
          </Typography>

          {onShowMore === undefined ? null : (
            <Button
              size="small"
              disabled={loading}
              onClick={() => void onShowMore()}
            >
              {t("activity.showMore")}
            </Button>
          )}
        </Stack>
      ) : null}
    </Stack>
  );
};
